import { useRef, useState } from "react";
import { Maximize2 } from "lucide-react";
import { Dialog } from "primereact/dialog";
import { useClickOutside } from "primereact/hooks";

import orderApi from "@/api/order";
import { useQuery } from "@/hooks/useQuery";
import OrderPropertiesRenderer from "./OrderPropertiesRenderer";

const OrderPropertiesModal = (props: { orderId: string }) => {
  const { orderId } = props;
  const [visible, setVisible] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const {
    data: orderDetail,
    loading,
    error,
  } = useQuery(["order-detail", orderId], () => orderApi.getOne(orderId), {
    enabled: visible && !!orderId,
    cacheTime: 300000,
  });

  const onHide = () => {
    setVisible(false);
  };

  useClickOutside(contentRef as React.RefObject<HTMLDivElement>, () => {
    onHide();
  });

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center w-full h-32">
          <i className="text-2xl text-gray-800 pi pi-spin pi-spinner"></i>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex justify-center items-center w-full h-32 text-red-500">
          <div className="text-center">
            <i className="mb-2 text-2xl pi pi-exclamation-triangle"></i>
            <p>Failed to load order details</p>
            <p className="text-sm text-gray-500">{error.message}</p>
          </div>
        </div>
      );
    }

    const properties = orderDetail?.data?.properties;
    if (!properties || properties.length === 0) {
      return (
        <div className="flex justify-center items-center w-full h-48 text-gray-500">
          <p>No properties available for this order.</p>
        </div>
      );
    }

    return <OrderPropertiesRenderer properties={properties} />;
  };

  return (
    <>
      <div
        className="flex gap-1 items-center px-2 py-1 ml-6 text-sm text-gray-600 rounded-md cursor-pointer jus6ify-center h-fit w-fit hover:border-gray-300 hover:bg-gray-100"
        title="View order properties"
        onClick={(e) => {
          e.stopPropagation();
          setVisible(true);
        }}
      >
        <Maximize2 size={18} />
      </div>
      <Dialog
        header={"Order properties"}
        visible={visible}
        modal
        style={{ width: "50vw" }}
        onHide={() => {
          if (!visible) return;
          onHide();
        }}
        draggable={false}
        resizable={false}
      >
        {renderContent()}
      </Dialog>
    </>
  );
};

export default OrderPropertiesModal;
